import { useState, useCallback, useRef, useEffect } from "react";

interface RetryConfig {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  retryableErrors?: string[];
}

interface RetryState {
  isRetrying: boolean;
  retryCount: number;
  lastError: string | null;
  isOffline: boolean;
}

interface UseEdgeFunctionRetryReturn<T> {
  execute: (fn: () => Promise<T>) => Promise<T>;
  retry: () => Promise<T | null>;
  reset: () => void;
  state: RetryState;
  canRetry: boolean;
}

const DEFAULT_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  retryableErrors: [
    "Failed to fetch",
    "NetworkError",
    "timeout",
    "TIMEOUT",
    "502",
    "503",
    "504",
    "Edge Function",
    "FunctionsFetchError",
    "FunctionsHttpError",
    "ERR_NETWORK",
    "ECONNRESET",
    "ECONNREFUSED",
  ],
};

/**
 * Hook für robuste Edge Function Aufrufe mit:
 * - Exponential Backoff Retry
 * - Offline-Detection
 * - Automatisches Reconnect bei Netzwerk-Recovery
 */
export function useEdgeFunctionRetry<T>(
  config: RetryConfig = {}
): UseEdgeFunctionRetryReturn<T> {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  const [state, setState] = useState<RetryState>({
    isRetrying: false,
    retryCount: 0,
    lastError: null,
    isOffline: !navigator.onLine,
  });

  const lastFnRef = useRef<(() => Promise<T>) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Offline/Online Detection
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, isOffline: false }));
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, isOffline: true }));
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const isRetryableError = useCallback(
    (error: unknown): boolean => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return mergedConfig.retryableErrors.some(retryable =>
        errorMessage.toLowerCase().includes(retryable.toLowerCase())
      );
    },
    [mergedConfig.retryableErrors]
  );

  const calculateDelay = useCallback(
    (attempt: number): number => {
      // Exponential backoff with jitter
      const exponentialDelay = mergedConfig.baseDelay * Math.pow(2, attempt);
      const jitter = Math.random() * 0.3 * exponentialDelay;
      return Math.min(exponentialDelay + jitter, mergedConfig.maxDelay);
    },
    [mergedConfig.baseDelay, mergedConfig.maxDelay]
  );

  const sleep = (ms: number): Promise<void> =>
    new Promise(resolve => setTimeout(resolve, ms));

  const execute = useCallback(
    async (fn: () => Promise<T>): Promise<T> => {
      lastFnRef.current = fn;
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setState(prev => ({
        ...prev,
        isRetrying: false,
        retryCount: 0,
        lastError: null,
      }));

      let lastError: unknown;
      let attempt = 0;

      while (attempt <= mergedConfig.maxRetries) {
        try {
          // Check if offline before attempting
          if (!navigator.onLine) {
            setState(prev => ({ ...prev, isOffline: true }));
            throw new Error("Du bist offline. Bitte prüfe deine Internetverbindung.");
          }

          const result = await fn();
          
          // Success - reset state
          setState(prev => ({
            ...prev,
            isRetrying: false,
            retryCount: 0,
            lastError: null,
          }));
          
          return result;
        } catch (error) {
          lastError = error;
          const errorMessage = error instanceof Error ? error.message : String(error);

          // Check if we should retry
          if (attempt < mergedConfig.maxRetries && isRetryableError(error)) {
            attempt++;
            const delay = calculateDelay(attempt);
            
            setState(prev => ({
              ...prev,
              isRetrying: true,
              retryCount: attempt,
              lastError: `Verbindungsproblem. Automatischer Retry ${attempt}/${mergedConfig.maxRetries}...`,
            }));

            console.log(
              `[EdgeFunctionRetry] Attempt ${attempt}/${mergedConfig.maxRetries}, waiting ${Math.round(delay)}ms...`,
              errorMessage
            );

            await sleep(delay);
          } else {
            // Not retryable or max retries reached
            setState(prev => ({
              ...prev,
              isRetrying: false,
              retryCount: attempt,
              lastError: errorMessage,
            }));
            throw error;
          }
        }
      }

      // Should not reach here, but just in case
      throw lastError;
    },
    [mergedConfig.maxRetries, isRetryableError, calculateDelay]
  );

  const retry = useCallback(async (): Promise<T | null> => {
    if (!lastFnRef.current) {
      console.warn("[EdgeFunctionRetry] No function to retry");
      return null;
    }

    setState(prev => ({
      ...prev,
      retryCount: 0,
      lastError: null,
    }));

    return execute(lastFnRef.current);
  }, [execute]);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    setState({
      isRetrying: false,
      retryCount: 0,
      lastError: null,
      isOffline: !navigator.onLine,
    });
    lastFnRef.current = null;
  }, []);

  const canRetry = !state.isRetrying && state.lastError !== null && !state.isOffline;

  return {
    execute,
    retry,
    reset,
    state,
    canRetry,
  };
}
